// --- Offline storage using idb-keyval ---
import { get, set, clear } from 'idb-keyval';

// --- Firebase imports ---
import { getDatabase, ref, set as fbSet, get as fbGet } from "firebase/database";
import { app } from './firebase.js';
import { getOrCreateFoodieId } from "./foodieId.js";

// Export commonly used services
const db = getDatabase(app, "https://ggapp-foodies.firebaseio.com/");

// Local keys for offline progress
const LOCAL_KEYS = {
  mustTry: "fogg_must_try",
  visited: "fogg_visited",
  quests: "fogg_quests",
  badges: "fogg_badges"
};

// Internal cache to reduce IndexedDB calls
let cache = {
  mustTry: null,
  visited: null,
  quests: null,
  badges: null
};

// --- Load from IndexedDB or return empty array ---
async function loadProgress(key) {
  if (cache[key]) return cache[key];
  const data = await get(key);
  cache[key] = data || [];
  return cache[key];
}

// --- Save to IndexedDB and update cache ---
async function saveProgress(key, data) {
  cache[key] = data;
  await set(key, data);
}

// --- Toggle Must Try ---
export async function toggleMustTry(placeId) {
  let mustTry = await loadProgress(LOCAL_KEYS.mustTry);
  if (!mustTry.includes(placeId)) {
    mustTry.push(placeId);
  } else {
    mustTry = mustTry.filter(id => id !== placeId);
  }
  await saveProgress(LOCAL_KEYS.mustTry, mustTry);
  await trySync();
  return mustTry;
}

// --- Toggle Visited ---
export async function toggleVisited(placeId) {
  let visited = await loadProgress(LOCAL_KEYS.visited);
  if (!visited.includes(placeId)) {
    visited.push(placeId);
  } else {
    visited = visited.filter(id => id !== placeId);
  }
  await saveProgress(LOCAL_KEYS.visited, visited);

  await trySync();
  return visited;
}

// --- Complete a Quest ---
export async function completeQuest(questId) {
  console.log("🚀 completeQuest called for", questId);

  let quests = await loadProgress(LOCAL_KEYS.quests);

  // Already completed? ✅ Save & return WITHOUT showing popup
  if (quests.includes(questId.toString())) {
    console.log("ℹ️ Quest already completed, skipping popup");
    return quests;
  }

  // ✅ NEW completion
  quests.push(questId.toString());
  await saveProgress(LOCAL_KEYS.quests, quests);
  await trySync();

  console.log(`🏆 NEW quest completed: ${questId}`);
  console.log('💾 Saving updated quest list:', quests);

  // 🎉 Show animation only for NEW quest completions
  showQuestUnlockAnimation();

  return quests;
}

export async function incompleteQuest(questId) {
  console.log("↩️ incompleteQuest called for", questId);

  let quests = await loadProgress(LOCAL_KEYS.quests);
  const questIdStr = questId.toString();

  // If quest is not in the list, do nothing
  if (!quests.includes(questIdStr)) {
    console.log("ℹ️ Quest not marked as completed, nothing to undo");
    return quests;
  }

  // 🧹 Remove it
  quests = quests.filter(id => id !== questIdStr);
  await saveProgress(LOCAL_KEYS.quests, quests);
  await trySync();

  console.log(`🗑️ Quest ${questId} marked as incomplete`);
  console.log('💾 Saving updated quest list:', quests);

  return quests;
}




// --- Earn a badge ---
export async function earnBadge(badgeId) {
  if (!badgeId || typeof badgeId !== "string") return; // ✅ guard against undefined

  let badges = await loadProgress(LOCAL_KEYS.badges);
  if (!badges.includes(badgeId)) {
    badges.push(badgeId);
    await saveProgress(LOCAL_KEYS.badges, badges);
    await trySync();
  }
  return badges;
}

// Show Quest Unlocked! :)

function showQuestUnlockAnimation() {
  const modal = document.createElement('div');
  modal.className = 'unlock-popup';
  
  // Get correct profile URL based on domain with anchor to quests section
  const basePath = window.location.hostname.includes('fogg.app') ? '/' : '/fogg/';
  const profileUrl = `${basePath}profile#completed-quests`;
  
  modal.innerHTML = `
    <div class="unlock-popup-inner">
      <a href="${profileUrl}"><img src="/images/quests/completed.png" alt="Quest Completed!" class="unlock-icon" /></a>
      <div class="unlock-text">
        <strong>Quest Completed!</strong><br>
        (Tap to view profile)
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // 🎉 Confetti when it appears
  import('./confetti.js').then(({ launchConfetti }) => launchConfetti());

  // Show with transition
  requestAnimationFrame(() => modal.classList.add('visible'));

  // ✅ Click anywhere on the popup → Go to profile
  modal.addEventListener('click', () => {
    window.location.href = profileUrl;
  });

  // Auto-remove after 4s if user doesn’t tap
  setTimeout(() => {
    if (document.body.contains(modal)) {
      modal.classList.remove('visible');
      setTimeout(() => modal.remove(), 500);
    }
  }, 4000);
}



// --- Get all progress for UI (merges local + Firebase remote) ---
export async function getFoodieProgress() {
  const local = {
    mustTry: await loadProgress(LOCAL_KEYS.mustTry),
    visited: await loadProgress(LOCAL_KEYS.visited),
    quests: await loadProgress(LOCAL_KEYS.quests),
    badges: await loadProgress(LOCAL_KEYS.badges)
  };

  try {
    const foodieId = await getOrCreateFoodieId();
    const snap = await fbGet(ref(getDatabase(app, "https://ggapp-foodies.firebaseio.com/"), `foodiesProgress/${foodieId}`));
    if (snap.exists()) {
      const remote = snap.val();
      
      return {
        mustTry: remote.mustTry || local.mustTry,
        visited: remote.visited || local.visited,
        quests: remote.quests || local.quests,
        badges: remote.badges || local.badges
      };
    }
  } catch (err) {
    console.warn("Firebase fetch failed, using local only", err);
  }

  return local;
}

// --- Try syncing when online ---
async function trySync() {
  if (!navigator.onLine) return;
  const foodieId = await getOrCreateFoodieId();
  await syncToFirebase(foodieId);
}

// --- Sync offline data to Firebase ---
export async function syncToFirebase(foodieId) {
  const db = getDatabase(app, "https://ggapp-foodies.firebaseio.com/");

  const mustTry = await loadProgress(LOCAL_KEYS.mustTry);
  const visited = await loadProgress(LOCAL_KEYS.visited);
  const quests = await loadProgress(LOCAL_KEYS.quests);
  const badges = await loadProgress(LOCAL_KEYS.badges);

  const foodieRef = ref(db, `foodiesProgress/${foodieId}`);
  await fbSet(foodieRef, {
    foodieId,
    mustTry,
    visited,
    quests,
    badges,
    updatedAt: Date.now()
  });
  console.log(`✅ Synced foodie progress for ${foodieId}`);
}

// --- Auto-sync when back online ---
window.addEventListener("online", async () => {
  const foodieId = await getOrCreateFoodieId();
  await syncToFirebase(foodieId);
});

// --- Clear all local progress ---
export async function clearAllProgress() {
  await clear();
  cache = { mustTry: null, visited: null, quests: null, badges: null };
}
